import { useCallback, useEffect, useState } from 'react'

import {
  actions as builderActions,
  WizardState
} from '../../../../tradebuilder-v2/modules/00-builder/state/builder-slice'
import { FundingSource } from '../../../../tradebuilder-v2/modules/02-terms/state/terms-slice'
import { useAppDispatch, useAppSelector } from '../../../../tradebuilder-v2/store/hooks'
import { useActions } from '../../../../tradebuilder-v2/store/use-actions'
import {
  actions as settlementActions,
  postSettlementApprove,
  postSettlementReject,
  TransferAsset
} from '../state/trade-settlement-slice'
import TradeSettlementStateless from '../templates/trade-settlement-stateless'

const TradeSettlement = () => {
  const builderState = useAppSelector(state => state.builder)
  const state = useAppSelector(state => state.tradeSettlement)
  const terms = useAppSelector(state => state.terms)
  const actions = useActions(settlementActions)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const dispatch = useAppDispatch()

  const { fundingSource } = terms.terms
  const isTransfer = fundingSource === FundingSource.Transfer

  const initialise = useCallback(
    (builder: WizardState, isTransfer: boolean) => {
      setIsLoading(true)
      const { portfolio } = builder.savedState.account.pendingAccountState
      const transferAssets = [] as TransferAsset[]
      if (isTransfer) {
        portfolio.targetWeights.forEach(targetWeight => {
          const units = builder.savedState.account.pendingAccountState.transferUnits?.find(
            transfer => transfer.assetId === targetWeight.asset.assetId
          ).units
          const transferAsset = {
            actualWeight: null,
            baseValue: null,
            id: targetWeight.asset.assetId,
            name: targetWeight.asset.name,
            originalPurchasePrice: null,
            price: null,
            targetWeight: targetWeight.weight,
            value: null,
            units
          }
          transferAssets.push(transferAsset)
        })
      }
      actions.reset()
      actions.initialise({
        userName: builder.userName,
        userType: builder.userType,
        userRoles: builder.userRoles,
        accountId: builder.accountId,
        currency: builder.currency,
        investment: isTransfer ? 0 : builder.savedState.pendingStateInvestment,
        isTransfer,
        approvalEvents: builder.approvalEvents,
        status: builder.status,
        assetsToTransfer: transferAssets
      })
    },
    [actions]
  )

  useEffect(() => {
    initialise(builderState, isTransfer)
  }, [isTransfer, initialise, builderState])

  useEffect(() => {
    if (state.accountId === builderState.selectedAccountId) {
      setIsLoading(false)
    }
  }, [builderState.selectedAccountId, state.accountId, state.approvalEvents])
  useEffect(() => {
    if (state.accountId === builderState.selectedAccountId && state.success === true) {
      dispatch(
        builderActions.refreshTradeStatus(state.accountId, {
          title: 'The trade has been successfully marked as settled'
        })
      )
    }
  }, [
    builderState.selectedAccountId,
    builderState.accountId,
    builderState.status,
    state.accountId,
    dispatch,
    state.success
  ])
  useEffect(() => {
    if (state.accountId === builderState.selectedAccountId && state.rejectSuccess === true) {
      dispatch(
        builderActions.refreshTradeStatus(state.accountId, {
          title: 'The trade has been rejected'
        })
      )
    }
  }, [
    builderState.selectedAccountId,
    builderState.accountId,
    builderState.status,
    state.accountId,
    dispatch,
    state.rejectSuccess
  ])

  const onApprove = () => {
    const transferUnits = []
    state.assetsToTransfer.forEach(asset => {
      const entry = {
        assetId: asset.id,
        units: asset.units,
        originalPurchasePrice: asset.originalPurchasePrice / 100 // TODO - get price scaling factor from server
      }
      transferUnits.push(entry)
    })
    const request = isTransfer ? { transferOfUnits: transferUnits } : null
    dispatch(postSettlementApprove(state.accountId, request))
  }
  const onReject = () => {
    dispatch(postSettlementReject(state.accountId, ''))
  }

  if (!isLoading) {
    return (
      <TradeSettlementStateless
        actions={actions}
        state={state}
        onSettlementApprove={onApprove}
        onSettlementReject={onReject}
      />
    )
  }

  return null
}

export default TradeSettlement
